import { Capacitor } from '@capacitor/core';
import { applyServerUnlocks } from './achievements-bridge';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// The web is same-origin → cookie auth. The native app (bundled Capacitor,
// cross-origin) can't send cookies, so it omits credentials and authenticates
// with a Bearer token (the session id) stored after login.
const IS_NATIVE = Capacitor.isNativePlatform();
const TOKEN_KEY = 'reformulat_auth_token';

export function setAuthToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* localStorage unavailable — ignore */
  }
}
export function getAuthToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

interface ApiError {
  message: string;
  status: number;
  // Structured AI limit error fields (HTTP 429 with error:'ai_limit')
  errorCode?: string;
  aiLimitType?: 'text' | 'cover';
  aiUsed?: number;
  aiLimit?: number;
}

/**
 * Error thrown when an AI quota limit is reached.
 * Consumers can check `error instanceof AiLimitError` to show a specific upgrade CTA.
 */
export class AiLimitError extends Error {
  readonly type: 'text' | 'cover';
  readonly used: number;
  readonly limit: number;

  constructor(type: 'text' | 'cover', used: number, limit: number, message: string) {
    super(message);
    this.name = 'AiLimitError';
    this.type = type;
    this.used = used;
    this.limit = limit;
  }
}

/**
 * Error thrown when a route requires a paid add-on (ia-studio / craft /
 * outlining) the user doesn't have active — HTTP 402 with error:'addon_required'.
 * Consumers can check `error instanceof AddonRequiredError` to show the
 * "Ver add-ons" upsell CTA instead of a generic failure toast.
 */
export class AddonRequiredError extends Error {
  readonly addonId: string;

  constructor(addonId: string, message: string) {
    super(message);
    this.name = 'AddonRequiredError';
    this.addonId = addonId;
  }
}

export interface AdminOverview {
  generatedAt: string;
  assumptions: { brlPerUsd: number; asaasFeeRate: number; note: string };
  users: {
    total: number; newThisMonth: number; newPrevMonth: number; paying: number;
    conversionRate: number; signupSeries: { day: string; count: number }[];
  };
  subscriptions: {
    byStatus: Record<string, number>; trialing: number;
    activePaidPlans: { plan: string; count: number }[];
  };
  addons: { addonId: string; cycle: string; count: number; monthlyEach: number; mrr: number }[];
  revenue: { mrrBrl: number; arrBrl: number };
  cost: { aiCogsUsd: number; aiCogsBrl: number; imageCogsUsd: number; requestsThisMonth: number };
  profit: { mrrBrl: number; cogsBrl: number; asaasFeesBrl: number; estimatedMonthlyProfitBrl: number; marginPct: number };
}

// ── AI chat (ghostwriter) — shared request/response shapes for the book AND
// manga conversational assistant (same textual-action protocol). ─────────────
export interface MangaChatContext {
  pageScript?: string;
  pageNumber?: number;
  totalPages?: number;
  cast?: string;
  premise?: string;
}

export interface AIChatAction {
  type: string;
  // Text actions
  searchText?: string;
  newText?: string;
  // Chapter actions
  title?: string;
  content?: string;
  position?: 'after_current' | 'before_current' | 'end' | 'start';
  sectionType?: string;
  chapterId?: string;
  order?: string[];
  fromChapterId?: string;
  toChapterId?: string;
  // Manga actions
  script?: string;
  name?: string;
  characterType?: string;
  description?: string;
  premise?: string;
}

export interface AIChatResponse {
  response: string;
  actions: AIChatAction[];
  tokensUsed: number;
}

export type BubbleType = 'speech' | 'thought' | 'shout' | 'narration' | 'caption' | 'whisper' | 'sfx';
export interface MangaBubble {
  id: string; type: BubbleType;
  x: number; y: number; w: number; h: number; // normalized 0..1
  /** Rotation in degrees around the box's own center — the WHOLE bubble
   * (body + tail + text) rotates together in every renderer (editor, reader,
   * PNG/PDF export, HTML export). Works for any bubble type, set via the
   * style panel's slider or the editor's own drag handle above the box.
   * Resize/tail handles stay on the UN-rotated axis-aligned box (see
   * `BubbleShape`'s comment in manga-editor.tsx for why). */
  rotation?: number;
  tail?: { x: number; y: number }; // tail tip, normalized (relative to bubble center)
  text: string; fontSize?: number; fontFamily?: string; align?: 'left' | 'center' | 'right';
  /** Who this line belongs to (character name, or NARRATOR/SFX) — the script is
   * the source of truth of the story; art is generated FROM it. */
  speaker?: string;
  /** Point-drawn body outline, normalized RELATIVE TO THE BUBBLE'S OWN BOX
   * (0..1 within w/h) — so moving/resizing the box only SCALES the body (no
   * redraw), while the tail (`tail` above) stays a separate geometric ray
   * toward its tip and is regenerated at render time, never deformed. */
  shape?: { body: { x: number; y: number }[] };
  // ── Vector bubble style (all optional/retro-compatible — see bubble-path.ts) ──
  /** Base width of the tail, in PAGE PIXELS (not a fraction). */
  tailWidth?: number;
  tailStyle?: 'straight' | 'curved';
  /** Manually mirrors the curved tail's bow to the other side. */
  tailFlip?: boolean;
  fillColor?: string;
  strokeColor?: string;
  strokeWidth?: number;
  textColor?: string;
  /** Inner text padding, in PAGE PIXELS. */
  padding?: number;
  fontStyle?: 'normal' | 'bold' | 'italic' | 'bold-italic';
  /** Number of spikes for the "shout" (scream) outline. */
  screamSpikes?: number;
  /** Corner radius for "narration"/"caption" (box) bubbles, in PAGE PIXELS. */
  borderRadius?: number;
  /** Body shape for 'speech'/'whisper' balloons — 'rect' draws a square manga
   * panel-style balloon (keeping the tail), default 'oval' when unset. */
  bodyShape?: 'oval' | 'rect';
}
export interface MangaPanel {
  id: string;
  points: { x: number; y: number }[]; // polygon corners, normalized 0..1
  imageKey?: string | null;
  imageUrl?: string | null; // resolved on read
  /** Author-uploaded rough sketch (stick-figure blueprint) for this panel — a
   * DIFFERENT state from the finished art (`imageKey`/`imageUrl`). Kept even
   * after the final art is generated, so the author can regenerate from it
   * again later. */
  sketchKey?: string | null;
  sketchUrl?: string | null; // resolved on read
  scale?: number; offsetX?: number; offsetY?: number; // art framing inside the panel
  flipX?: boolean; flipY?: boolean; // mirror the art horizontally / vertically
  border?: number;
  /** Persisted page script for THIS panel: what happens visually + which cast
   * members appear. Lets us (re)generate the art of any panel — or the whole
   * page — from the written story, at any time. */
  script?: { description: string; castIds: string[] };
}
export interface MangaLayout { panels: MangaPanel[]; bubbles: MangaBubble[]; }
export interface MangaPage {
  id: string; projectId: string; chapterId: string | null; order: number;
  imageKey: string | null; imageUrl?: string | null; width: number; height: number;
  layout: MangaLayout; createdAt: string; updatedAt: string;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const token = getAuthToken();
    const config: RequestInit = {
      ...options,
      // Native omits cookies (Bearer auth); web includes the session cookie.
      credentials: IS_NATIVE ? 'omit' : 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(IS_NATIVE && token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      let data: Record<string, unknown> = {};
      try {
        data = await response.json();
      } catch {
        // ignore parse error — data stays empty
      }

      // Structured AI limit response: { error: 'ai_limit', type, used, limit, message }
      if (response.status === 429 && data.error === 'ai_limit') {
        throw new AiLimitError(
          data.type as 'text' | 'cover',
          data.used as number,
          data.limit as number,
          (data.message as string) || 'Limite de IA atingido.'
        );
      }

      // Structured add-on paywall response: { error: 'addon_required', addonId, message }
      if (response.status === 402 && data.error === 'addon_required') {
        throw new AddonRequiredError(
          (data.addonId as string) || '',
          (data.message as string) || 'Este recurso requer um add-on ativo.'
        );
      }

      const error: ApiError = {
        message: (data.error as string) || (data.message as string) || response.statusText || 'Request failed',
        status: response.status,
      };

      throw error;
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  // Auth endpoints
  async login(email: string, password: string) {
    const res = await this.request<{ user: User; token?: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    // Native app: persist the Bearer token for subsequent requests.
    if (IS_NATIVE && res.token) setAuthToken(res.token);
    return res;
  }

  async register(data: { name: string; email: string; password: string }) {
    const res = await this.request<{ user: User; token?: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (IS_NATIVE && res.token) setAuthToken(res.token);
    return res;
  }

  async logout() {
    const res = await this.request<void>('/auth/logout', {
      method: 'POST',
    });
    setAuthToken(null);
    return res;
  }

  async getMe() {
    return this.request<{ user: User | null }>('/auth/me');
  }

  async updateProfile(data: { name?: string; avatarUrl?: string | null }) {
    return this.request<{ success: boolean }>('/auth/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /** Save the user's own OpenAI API key (BYOK). Key is encrypted at rest on the server. */
  async setOpenAIKey(key: string) {
    return this.request<{ success: boolean; hasOwnKey: boolean; keyMaskedSuffix: string }>(
      '/auth/openai-key',
      {
        method: 'POST',
        body: JSON.stringify({ key }),
      }
    );
  }

  /** Remove the user's own OpenAI API key, reverting to the platform key + quota. */
  async clearOpenAIKey() {
    return this.request<{ success: boolean; hasOwnKey: boolean }>('/auth/openai-key', {
      method: 'DELETE',
    });
  }

  async requestPasswordReset(email: string) {
    return this.request<{ success: boolean }>('/auth/request-password-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, password: string) {
    return this.request<{ success: boolean }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  async requestEmailVerification() {
    return this.request<{ success: boolean }>('/auth/request-email-verification', {
      method: 'POST',
    });
  }

  async verifyEmail(token: string) {
    return this.request<{ success: boolean }>('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  // Projects endpoints
  async getProjects() {
    return this.request<{ projects: Project[] }>('/projects');
  }

  async createProject(data: CreateProjectData) {
    return this.request<{ project: Project }>('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getProject(id: string) {
    return this.request<{ project: Project }>(`/projects/${id}`);
  }

  async updateProject(id: string, data: Partial<Project>) {
    return this.request<{ project: Project }>(`/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteProject(id: string) {
    return this.request<void>(`/projects/${id}`, {
      method: 'DELETE',
    });
  }

  // Chapters endpoints
  async getChapters(projectId: string) {
    return this.request<{ chapters: Chapter[] }>(`/chapters?projectId=${projectId}`);
  }

  async createChapter(data: CreateChapterData) {
    return this.request<{ chapter: Chapter }>('/chapters', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateChapter(id: string, data: Partial<Chapter>) {
    const res = await this.request<{ chapter: Chapter; unlocked?: string[] }>(`/chapters/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    if (res.unlocked) applyServerUnlocks(res.unlocked);
    return res;
  }

  async deleteChapter(id: string) {
    return this.request<void>(`/chapters/${id}`, {
      method: 'DELETE',
    });
  }

  async reorderChapters(projectId: string, chapterIds: string[]) {
    return this.request<void>('/chapters/reorder', {
      method: 'POST',
      body: JSON.stringify({ projectId, chapterIds }),
    });
  }

  // Chapter version history endpoints
  async getChapterVersions(chapterId: string) {
    return this.request<{ versions: ChapterVersionMeta[] }>(`/chapters/${chapterId}/versions`);
  }

  async getChapterVersion(chapterId: string, versionId: string) {
    return this.request<{ version: ChapterVersionFull }>(`/chapters/${chapterId}/versions/${versionId}`);
  }

  async createChapterVersion(chapterId: string) {
    return this.request<{ version: ChapterVersionFull }>(`/chapters/${chapterId}/versions`, {
      method: 'POST',
    });
  }

  async restoreChapterVersion(chapterId: string, versionId: string) {
    return this.request<{ chapter: Chapter }>(`/chapters/${chapterId}/versions/${versionId}/restore`, {
      method: 'POST',
    });
  }

  // Subscription endpoints
  async getSubscription() {
    return this.request<{ subscription: Subscription | null }>('/subscriptions');
  }

  async getPlans() {
    return this.request<{ plans: Plan[] }>('/subscriptions/plans');
  }

  async createSubscription(planId: string) {
    return this.request<{ paymentUrl: string }>('/subscriptions', {
      method: 'POST',
      body: JSON.stringify({ planId }),
    });
  }

  async cancelSubscription() {
    return this.request<void>('/subscriptions', {
      method: 'DELETE',
    });
  }

  async getSubscriptionUsage() {
    return this.request<{ usage: SubscriptionUsage }>('/subscriptions/usage');
  }

  async getCurrentSubscription() {
    return this.request<{ subscription: Subscription | null }>('/subscriptions/current');
  }

  // Goals endpoints
  async getGoals(projectId: string) {
    return this.request<{ goals: WritingGoal[] }>(`/goals?projectId=${projectId}`);
  }

  async createGoal(data: CreateGoalData) {
    return this.request<{ goal: WritingGoal }>('/goals', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateGoal(id: string, data: Partial<WritingGoal>) {
    return this.request<{ goal: WritingGoal }>(`/goals/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async recordSession(data: { projectId: string; wordsWritten: number; duration: number }) {
    return this.request<void>('/goals/session', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getDailyStats(projectId?: string) {
    const query = projectId ? `?projectId=${projectId}` : '';
    return this.request<{ stats: DailyStats[] }>(`/goals/stats${query}`);
  }

  // Messages endpoints
  async getUnreadCount() {
    return this.request<{ unreadCount: number }>('/messages/unread-count');
  }

  async getConversations() {
    return this.request<{ conversations: Conversation[] }>('/messages/conversations');
  }

  async createConversation(userId: string) {
    return this.request<{ conversation: Conversation; isNew: boolean }>('/messages/conversations', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async sendMessage(conversationId: string, content: string, metadata?: Record<string, unknown>) {
    return this.request<{ message: unknown }>(`/messages/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, ...(metadata ? { metadata } : {}) }),
    });
  }

  async getProfessionalByUserId(userId: string) {
    return this.request<{ professionals: Array<{ id: string; type: string; title: string; bio?: string; rating?: number; user: { id: string; name: string; avatarUrl?: string } }> }>(`/professionals?userId=${userId}`).then((res) => res.professionals.find((p) => p.user.id === userId) ?? null);
  }

  // AI endpoints
  async getAIQuota() {
    return this.request<{ allowed: boolean; used: number; limit: number }>('/ai/quota');
  }

  async getAiUsage() {
    return this.request<{ text: { used: number; limit: number }; cover: { used: number; limit: number } }>('/ai/usage');
  }

  /** How many AI actions a specific book has consumed (all time). */
  async getProjectAiUsage(projectId: string) {
    return this.request<{ actions: number }>(`/ai/usage/project/${projectId}`);
  }

  async aiAssist(data: {
    projectId: string;
    chapterId?: string;
    selection: string;
    action:
      | 'rewrite'
      | 'tone'
      | 'style'
      | 'expand'
      | 'shorten'
      | 'continue'
      | 'grammar'
      | 'simplify'
      | 'show-not-tell'
      | 'describe'
      | 'custom';
    options?: {
      tone?: string;
      style?: string;
      targetLength?: number;
      language?: string;
    };
    /** For expand/shorten: how much to change */
    amount?: 'slight' | 'moderate' | 'strong';
    /** For action='custom': free-form instruction from the user */
    customPrompt?: string;
    /** Optional model override ('gpt-5-mini' | 'gpt-5'). Server validates the allowlist. */
    model?: string;
  }): Promise<string> {
    const res = await this.request<{ result: string }>('/ai/assist', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.result;
  }

  /**
   * Checks whether an already-written chapter still matches a new Structure
   * (outline board) beat, and — if the author accepts — returns a rewritten
   * version of the chapter aligned with the new beat.
   */
  async reconcileChapter(
    projectId: string,
    chapterId: string,
    structureChange: string,
    model?: string
  ): Promise<{ diverges: boolean; summary: string; reconciledContent: string }> {
    return this.request<{ diverges: boolean; summary: string; reconciledContent: string }>('/ai/reconcile', {
      method: 'POST',
      body: JSON.stringify({ projectId, chapterId, structureChange, model }),
    });
  }

  /**
   * Inline "type-ahead" continuation suggestion — always grounded in the book
   * (Story Bible/universe, premise, chapters), never generic. `length`
   * controls how much text comes back: 'short' finishes the sentence,
   * 'long' continues for about one short paragraph.
   */
  async autocomplete(data: {
    projectId: string;
    chapterId?: string;
    before: string;
    length: 'short' | 'long';
  }): Promise<{ suggestion: string }> {
    return this.request<{ suggestion: string }>('/ai/autocomplete', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async processText(
    text: string,
    operation: 'improve' | 'expand' | 'condense' | 'grammar',
    language?: string,
    projectId?: string,
  ) {
    return this.request<{ result: string; tokensUsed: number }>('/ai/process', {
      method: 'POST',
      body: JSON.stringify({ text, operation, language, projectId }),
    });
  }

  async adjustTone(text: string, tone: 'formal' | 'casual' | 'poetic' | 'humorous' | 'dramatic') {
    return this.request<{ result: string; tokensUsed: number }>('/ai/tone', {
      method: 'POST',
      body: JSON.stringify({ text, tone }),
    });
  }

  async transcribeAudio(file: File) {
    const formData = new FormData();
    formData.append('audio', file);

    return this.request<{ text: string; duration?: number }>('/ai/transcribe', {
      method: 'POST',
      body: formData,
      headers: {}, // Remove Content-Type for FormData
    });
  }

  async generateOutline(transcript: string, chapters?: number, style?: string) {
    return this.request<{ outline: string; tokensUsed: number }>('/ai/outline', {
      method: 'POST',
      body: JSON.stringify({ transcript, chapters, style }),
    });
  }

  async generateDraft(
    outline: string,
    sectionTitle: string,
    wordCount?: number,
    style?: string,
    projectId?: string,
    model?: string,
  ) {
    return this.request<{ draft: string; tokensUsed: number }>('/ai/draft', {
      method: 'POST',
      body: JSON.stringify({ outline, sectionTitle, wordCount, style, projectId, model }),
    });
  }

  /** Suggest a short chapter title from its prose (used when the user gives none). */
  async suggestTitle(text: string, projectId?: string, exampleTitles?: string[]) {
    return this.request<{ title: string }>('/ai/suggest-title', {
      method: 'POST',
      body: JSON.stringify({
        text,
        projectId,
        ...(exampleTitles && exampleTitles.length > 0 ? { exampleTitles } : {}),
      }),
    });
  }

  /** Extract new named characters from prose that are not yet in the Story Bible. */
  async extractCharacters(text: string, existingNames?: string[], projectId?: string) {
    return this.request<{ characters: Array<{ name: string; description: string }> }>(
      '/ai/extract-characters',
      {
        method: 'POST',
        body: JSON.stringify({ text, existingNames, projectId }),
      }
    );
  }

  async sendAIChat(data: {
    message: string;
    chapterContent?: string;
    selection?: string;
    projectId?: string;
    currentChapterId?: string;
    history?: Array<{ role: 'user' | 'assistant'; content: string }>;
    /** Optional model override ('gpt-5-mini' | 'gpt-5'). Server validates the allowlist. */
    model?: string;
    /** MANGA/HQ mode — the ghostwriter fills the page script / Universe / premise. */
    mangaMode?: boolean;
    mangaContext?: MangaChatContext;
  }) {
    return this.request<AIChatResponse>('/ai/chat', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async sendAIChatAudio(data: {
    audio: Blob;
    chapterContent?: string;
    selection?: string;
    projectId?: string;
    currentChapterId?: string;
    history?: Array<{ role: 'user' | 'assistant'; content: string }>;
    /** Optional model override ('gpt-5-mini' | 'gpt-5'). Server validates the allowlist. */
    model?: string;
    /** MANGA/HQ mode — the ghostwriter fills the page script / Universe / premise. */
    mangaMode?: boolean;
    mangaContext?: MangaChatContext;
  }) {
    const formData = new FormData();
    formData.append('audio', data.audio, 'audio.webm');
    if (data.chapterContent) {
      formData.append('chapterContent', data.chapterContent);
    }
    if (data.selection) {
      formData.append('selection', data.selection);
    }
    if (data.projectId) {
      formData.append('projectId', data.projectId);
    }
    if (data.currentChapterId) {
      formData.append('currentChapterId', data.currentChapterId);
    }
    if (data.history) {
      formData.append('history', JSON.stringify(data.history));
    }
    if (data.model) {
      formData.append('model', data.model);
    }
    if (data.mangaMode) {
      formData.append('mangaMode', 'true');
    }
    if (data.mangaContext) {
      formData.append('mangaContext', JSON.stringify(data.mangaContext));
    }

    return this.request<AIChatResponse & {
      transcribedMessage: string;
    }>('/ai/chat/audio', {
      method: 'POST',
      body: formData,
      headers: {}, // Remove Content-Type for FormData
    });
  }

  // Reviews endpoints
  async getReviews(professionalId: string) {
    return this.request<{ reviews: Review[] }>(`/reviews/professional/${professionalId}`);
  }

  async createReview(data: { hireId: string; rating: number; title?: string; content?: string; isPublic?: boolean }) {
    return this.request<{ review: Review }>('/reviews', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMyReviews() {
    return this.request<{ given: Review[]; received: Review[] }>('/reviews/me');
  }

  // Portfolio endpoints
  async getPortfolio(professionalId: string) {
    return this.request<{ items: PortfolioItem[] }>(`/professionals/${professionalId}/portfolio`);
  }

  async addPortfolioItem(data: { title: string; description?: string; imageUrl?: string; projectUrl?: string; category?: string }) {
    return this.request<{ item: PortfolioItem }>('/professionals/me/portfolio', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deletePortfolioItem(itemId: string) {
    return this.request<void>(`/professionals/me/portfolio/${itemId}`, {
      method: 'DELETE',
    });
  }

  // Fact-checking endpoints
  async factCheckText(text: string) {
    return this.request<{
      claims: FactClaim[];
      verifications: FactVerification[];
      summary: {
        total: number;
        verified: number;
        unverified: number;
        incorrect: number;
      };
    }>('/ai/fact-check', {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  async quickFactCheck(statement: string) {
    return this.request<{
      isAccurate: boolean | null;
      confidence: number;
      explanation: string;
      correction?: string;
    }>('/ai/fact-check/quick', {
      method: 'POST',
      body: JSON.stringify({ statement }),
    });
  }

  async extractClaims(text: string) {
    return this.request<{ claims: FactClaim[] }>('/ai/fact-check/extract', {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  // Embeddings endpoints
  async indexChapter(chapterId: string, projectId: string) {
    return this.request<{ success: boolean; message: string }>('/ai/embeddings/chapter', {
      method: 'POST',
      body: JSON.stringify({ chapterId, projectId }),
    });
  }

  async indexProject(projectId: string) {
    return this.request<{ success: boolean; message: string }>('/ai/embeddings/project', {
      method: 'POST',
      body: JSON.stringify({ projectId }),
    });
  }

  async getEmbeddingsStatus(projectId: string) {
    return this.request<{
      indexed: boolean;
      stats: { totalChunks: number; totalTokens: number; indexedChapters: number } | null;
    }>(`/ai/embeddings/status/${projectId}`);
  }

  // Consistency checking endpoints
  async checkProjectConsistency(projectId: string) {
    return this.request<ConsistencyReport>('/ai/consistency/project', {
      method: 'POST',
      body: JSON.stringify({ projectId }),
    });
  }

  async checkChapterConsistency(chapterId: string, content: string, chapterTitle: string) {
    return this.request<{
      entities: Entity[];
      internalIssues: Inconsistency[];
    }>('/ai/consistency/chapter', {
      method: 'POST',
      body: JSON.stringify({ chapterId, content, chapterTitle }),
    });
  }

  async checkContentConsistency(content: string, projectId: string, currentChapterId?: string) {
    return this.request<{
      potentialIssues: Inconsistency[];
      relatedEntities: Entity[];
    }>('/ai/consistency/check', {
      method: 'POST',
      body: JSON.stringify({ content, projectId, currentChapterId }),
    });
  }

  async extractEntities(content: string, chapterId?: string, chapterTitle?: string) {
    return this.request<{ entities: Entity[] }>('/ai/consistency/entities', {
      method: 'POST',
      body: JSON.stringify({ content, chapterId, chapterTitle }),
    });
  }

  async generateCover(data: {
    prompt: string;
    title?: string;
    author?: string;
    genre?: string;
    projectId?: string;
  }): Promise<string[]> {
    const res = await this.request<{ covers: string[] }>('/ai/generate-cover', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.covers;
  }

  async generateCoverPrompt(projectId: string): Promise<string> {
    const res = await this.request<{ description: string }>('/ai/cover-prompt', {
      method: 'POST',
      body: JSON.stringify({ projectId }),
    });
    return res.description;
  }

  async illustrateScene(data: {
    projectId: string;
    chapterId?: string;
    selection: string;
    style?: string;
  }): Promise<{ imageUrl: string; prompt: string }> {
    return this.request<{ imageUrl: string; prompt: string }>('/ai/illustrate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Notifications endpoints
  async getNotifications() {
    return this.request<{ notifications: AppNotification[]; unreadCount: number }>('/notifications');
  }

  async getUnreadNotificationCount() {
    return this.request<{ count: number }>('/notifications/unread-count');
  }

  async markNotificationRead(id: string) {
    return this.request<void>(`/notifications/${id}/read`, {
      method: 'POST',
    });
  }

  async markAllNotificationsRead() {
    return this.request<void>('/notifications/read-all', {
      method: 'POST',
    });
  }

  async registerFcmToken(token: string) {
    return this.request<void>('/notifications/fcm-token', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async unregisterFcmToken(token: string) {
    return this.request<void>('/notifications/fcm-token', {
      method: 'DELETE',
      body: JSON.stringify({ token }),
    });
  }

  // Owner dashboard (admin-only; backend gates by ADMIN_EMAIL)
  async getAdminOverview() {
    return this.request<AdminOverview>('/admin/overview');
  }

  // ── Manga / comic editor ──────────────────────────────────────────────────
  async getMangaPages(projectId: string) {
    return this.request<{ pages: MangaPage[]; readingDirection: 'ltr' | 'rtl' }>(
      `/manga/pages?projectId=${projectId}`,
    );
  }
  async createMangaPage(projectId: string, opts?: { image?: string; imageKey?: string; width?: number; height?: number }) {
    return this.request<{ page: MangaPage }>('/manga/pages', {
      method: 'POST',
      body: JSON.stringify({ projectId, ...opts }),
    });
  }
  async updateMangaPage(
    id: string,
    updates: { layout?: MangaLayout; order?: number; image?: string; imageKey?: string; width?: number; height?: number },
  ) {
    return this.request<{ page: MangaPage }>(`/manga/pages/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }
  async deleteMangaPage(id: string) {
    return this.request<{ success: boolean }>(`/manga/pages/${id}`, { method: 'DELETE' });
  }
  async reorderMangaPages(projectId: string, order: string[]) {
    return this.request<{ success: boolean }>('/manga/pages/reorder', {
      method: 'POST',
      body: JSON.stringify({ projectId, order }),
    });
  }
  async uploadMangaImage(projectId: string, image: string) {
    return this.request<{ imageKey: string; imageUrl: string }>('/manga/upload', {
      method: 'POST',
      body: JSON.stringify({ projectId, image }),
    });
  }
  async generateMangaImage(
    projectId: string,
    prompt: string,
    opts?: {
      referenceIds?: string[];
      size?: '1024x1024' | '1024x1536' | '1536x1024';
      /** Wasabi key of a rough sketch (see `MangaPanel.sketchKey`) — used as the
       * FIRST reference image (composition/pose blueprint), the others still
       * lock character appearances. */
      sketchKey?: string;
    },
  ) {
    return this.request<{ imageUrl: string; imageKey: string; usedReferences: number }>('/manga/generate', {
      method: 'POST',
      body: JSON.stringify({ projectId, prompt, ...opts }),
    });
  }

  /**
   * AI-generate the page SCRIPT for a manga page: for each panel (in reading
   * order), a visual description for the artist + the dialogue lines, plus
   * the Story Bible entry ids whose canonical names appear in that panel
   * (ready to preselect as references when generating the art).
   */
  async generateMangaScript(
    projectId: string,
    sceneDescription: string,
    panelCount: number,
    castIds?: string[],
    opts?: {
      mode?: 'full' | 'dialogues' | 'ghostwriter';
      /** For 'dialogues': just the fixed descriptions. For 'ghostwriter': the
       * author's current draft per panel — description AND dialogues already
       * written (possibly empty/partial) — preserved by the server, not
       * rewritten from scratch. */
      existingPanels?: Array<{ description: string; dialogues?: Array<{ speaker: string; type: BubbleType; text: string }> }>;
      /** Zero-based index of the page being written — the server excludes it
       * from "story so far" and anchors the model to "page X of Y" so a fresh
       * page continues the story instead of copying the previous one. */
      pageIndex?: number;
    },
  ) {
    return this.request<{
      panels: Array<{
        description: string;
        castIds: string[];
        dialogues: Array<{ speaker: string; type: BubbleType; text: string }>;
      }>;
      newCast?: Array<{ id: string; name: string; type: string }>;
    }>('/manga/script', {
      method: 'POST',
      body: JSON.stringify({
        projectId, sceneDescription, panelCount,
        ...(castIds?.length ? { castIds } : {}),
        ...(opts?.mode ? { mode: opts.mode } : {}),
        ...(opts?.existingPanels?.length ? { existingPanels: opts.existingPanels } : {}),
        ...(typeof opts?.pageIndex === 'number' ? { pageIndex: opts.pageIndex } : {}),
      }),
    });
  }

  // Add-ons endpoints
  async getAddons() {
    return this.request<{
      addons: UserAddon[];
      coverCredits: number;
      /** Computed server-side (purchase, bundle, or active trial) — used to
       *  gate client-only features that have no server route, e.g. EPUB/HTML
       *  export (see export-view.tsx). */
      entitlements: { iaStudio: boolean; craft: boolean; outlining: boolean };
    }>('/addons');
  }

  async activateAddon(addonId: string, cycle: 'monthly' | 'annual', cpfCnpj?: string) {
    return this.request<{ status: string; addonId: string; paymentUrl: string | null }>(
      '/addons/activate',
      {
        method: 'POST',
        body: JSON.stringify({ addonId, cycle, cpfCnpj }),
      }
    );
  }

  async cancelAddon(addonId: string) {
    return this.request<{ success: boolean; addonId: string; activeUntil: string }>(
      `/addons/${addonId}`,
      { method: 'DELETE' }
    );
  }

  async buyCoverCredits(packs: number, cpfCnpj?: string) {
    return this.request<{ status: string; packs: number; credits: number; paymentUrl: string | null }>(
      '/addons/credits/purchase',
      {
        method: 'POST',
        body: JSON.stringify({ packs, cpfCnpj }),
      }
    );
  }

  // ============================================
  // Story Bible endpoints
  // ============================================

  async getBibleEntries(projectId: string) {
    return this.request<{ entries: BibleEntry[] }>(`/bible/${projectId}`);
  }

  async createBibleEntry(projectId: string, data: CreateBibleEntryData) {
    const res = await this.request<{ entry: BibleEntry; unlocked?: string[] }>(`/bible/${projectId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (res.unlocked) applyServerUnlocks(res.unlocked);
    return res;
  }

  async updateBibleEntry(projectId: string, entryId: string, data: Partial<CreateBibleEntryData>) {
    return this.request<{ entry: BibleEntry }>(`/bible/${projectId}/${entryId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteBibleEntry(projectId: string, entryId: string) {
    return this.request<{ success: boolean }>(`/bible/${projectId}/${entryId}`, {
      method: 'DELETE',
    });
  }

  /** Expand a seed into a rich entry (content + attributes) WITHOUT saving. May also
   * auto-create Story Bible stubs for related characters/places the AI surfaced. */
  async expandBibleEntry(projectId: string, input: { type: string; name: string; seed?: string }) {
    return this.request<{
      content: string;
      attributes: Record<string, string>;
      relatedAdded?: Array<{ id: string; name: string; type: string }>;
    }>(`/bible/${projectId}/expand`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async autofillBible(projectId: string, force = false) {
    return this.request<{ inserted: number; entries: BibleEntry[] }>(`/bible/${projectId}/autofill`, {
      method: 'POST',
      body: JSON.stringify({ force }),
    });
  }

  /** Generate a reference image (gpt-image-2) for a bible entry from its canon. */
  async generateBibleEntryImage(
    projectId: string,
    entryId: string,
    opts?: { prompt?: string; style?: string }
  ) {
    return this.request<{ entry: BibleEntry }>(`/bible/${projectId}/${entryId}/generate-image`, {
      method: 'POST',
      body: JSON.stringify(opts ?? {}),
    });
  }

  // ============================================
  // Outline Board endpoints
  // ============================================

  async getBoard(projectId: string) {
    return this.request<{ columns: BoardColumn[] }>(`/board/${projectId}`);
  }

  async createColumn(projectId: string, title: string) {
    return this.request<{ column: BoardColumn }>(`/board/${projectId}/columns`, {
      method: 'POST',
      body: JSON.stringify({ title }),
    });
  }

  async updateColumn(projectId: string, columnId: string, title: string) {
    return this.request<{ column: BoardColumn }>(`/board/${projectId}/columns/${columnId}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    });
  }

  async deleteColumn(projectId: string, columnId: string) {
    return this.request<{ success: boolean }>(`/board/${projectId}/columns/${columnId}`, {
      method: 'DELETE',
    });
  }

  async reorderColumns(projectId: string, columnIds: string[]) {
    return this.request<{ success: boolean }>(`/board/${projectId}/columns/reorder`, {
      method: 'POST',
      body: JSON.stringify({ columnIds }),
    });
  }

  async createCard(projectId: string, data: CreateCardData) {
    return this.request<{ card: BoardCard }>(`/board/${projectId}/cards`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCard(projectId: string, cardId: string, data: UpdateCardData) {
    return this.request<{ card: BoardCard }>(`/board/${projectId}/cards/${cardId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteCard(projectId: string, cardId: string) {
    return this.request<{ success: boolean }>(`/board/${projectId}/cards/${cardId}`, {
      method: 'DELETE',
    });
  }

  async moveCard(projectId: string, cardId: string, toColumnId: string, toIndex: number) {
    return this.request<{ card: BoardCard }>(`/board/${projectId}/cards/move`, {
      method: 'POST',
      body: JSON.stringify({ cardId, toColumnId, toIndex }),
    });
  }

  async reorderCards(projectId: string, columnId: string, cardIds: string[]) {
    return this.request<{ success: boolean }>(`/board/${projectId}/cards/reorder`, {
      method: 'POST',
      body: JSON.stringify({ columnId, cardIds }),
    });
  }

  // ============================================
  // Members / Collaboration endpoints
  // ============================================

  async getMembers(projectId: string) {
    return this.request<{ owner: ProjectMemberOwner; members: ProjectMemberEntry[] }>(
      `/projects/${projectId}/members`
    );
  }

  async inviteMember(projectId: string, data: { email: string; role: MemberRole }) {
    return this.request<{ success: boolean; memberId: string }>(`/projects/${projectId}/members`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMemberRole(projectId: string, memberId: string, role: MemberRole) {
    return this.request<{ success: boolean }>(`/projects/${projectId}/members/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }

  async removeMember(projectId: string, memberId: string) {
    return this.request<{ success: boolean }>(`/projects/${projectId}/members/${memberId}`, {
      method: 'DELETE',
    });
  }

  async getInvite(token: string) {
    return this.request<{ invite: InviteInfo }>(`/invites/${token}`);
  }

  async acceptInvite(token: string) {
    return this.request<{ success: boolean; projectId: string; alreadyActive?: boolean }>(
      `/invites/${token}/accept`,
      { method: 'POST' }
    );
  }

  // ============================================
  // Achievements endpoints
  // ============================================

  /** All achievement keys unlocked by the current user (server-owned, idempotent). */
  async getAchievements() {
    return this.request<{ achievements: Array<{ key: string; payload: unknown; unlockedAt: string }> }>('/achievements');
  }
}

export interface Conversation {
  id: string;
  createdAt: string;
  updatedAt: string;
  lastMessage?: {
    id: string;
    content: string;
    createdAt: string;
    sender: {
      id: string;
      name: string;
    };
  };
  otherParticipants: Array<{
    userId: string;
    user: {
      id: string;
      name: string;
      avatarUrl?: string;
    };
  }>;
  unreadCount: number;
}

// Types
export interface User {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string | null;
  createdAt: string;
  /** True when the user has saved their own OpenAI API key (BYOK). */
  hasOwnKey?: boolean;
  /** Last 4 characters of the saved key, for display only. Never the full key. */
  keyMaskedSuffix?: string;
}

export interface Project {
  id: string;
  title: string;
  subtitle?: string;
  author?: string;
  description?: string;
  genre?: string;
  language: string;
  coverImage?: string;
  isbn?: string;
  publisher?: string;
  publishDate?: string;
  copyright?: string;
  keywords?: string[];
  kind?: 'book' | 'manga';
  readingDirection?: 'ltr' | 'rtl';
  // Project-wide "Estilo visual" — a style clause appended to every AI image
  // generation (bible reference sheets, scene illustrations, cover, manga panels).
  mangaStyle?: string | null;
  createdAt: string;
  updatedAt: string;
  chapters?: Chapter[];
  writingGoal?: WritingGoal | null;
}

export interface Chapter {
  id: string;
  projectId: string;
  title: string;
  content: string;
  order: number;
  wordCount: number;
  sectionType: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Subscription {
  id: string;
  plan: 'free' | 'starter' | 'professional' | 'unlimited';
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'expired';
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  trialEndsAt?: string;
  cancelAtPeriodEnd?: boolean;
  planDetails?: {
    name: string;
    price: number;
    projects: number;
    wordsPerMonth: number;
    features: string[];
  };
}

export interface SubscriptionUsage {
  projects: {
    used: number;
    limit: number;
    unlimited: boolean;
  };
  words: {
    used?: number;
    limit: number;
    unlimited: boolean;
  };
}

export interface Plan {
  id: string;
  name: string;
  price: number;
  interval: 'monthly' | 'yearly';
  features: string[];
}

export interface WritingGoal {
  id: string;
  projectId: string;
  type: 'daily' | 'project' | 'session';
  targetWords: number;
  currentWords: number;
  deadline?: string;
}

export interface DailyStats {
  date: string;
  wordsWritten: number;
  timeSpent: number;
  sessionsCount: number;
}

export interface CreateProjectData {
  title: string;
  description?: string;
  genre?: string;
  language?: string;
  template?: string;
  kind?: 'book' | 'manga';
}

export interface CreateChapterData {
  projectId: string;
  title: string;
  sectionType?: string;
  content?: string;
}

interface CreateGoalData {
  projectId: string;
  type: 'daily' | 'project' | 'session';
  targetWords: number;
  deadline?: string;
}

export interface Review {
  id: string;
  hireId: string;
  reviewerId: string;
  revieweeId: string;
  rating: number;
  title?: string;
  content?: string;
  isPublic: boolean;
  createdAt: string;
  reviewer?: { id: string; name: string; avatarUrl?: string };
  reviewee?: { id: string; name: string; avatarUrl?: string };
}

export interface PortfolioItem {
  id: string;
  professionalId: string;
  title: string;
  description?: string;
  imageUrl?: string;
  projectUrl?: string;
  category?: string;
  order: number;
  createdAt: string;
}

// Fact-checking types
export interface FactClaim {
  text: string;
  type: 'date' | 'historical' | 'scientific' | 'statistic' | 'person' | 'place' | 'other';
  context: string;
}

export interface FactVerification {
  claim: FactClaim;
  verified: boolean | null;
  confidence: number;
  explanation: string;
  sources: string[];
  suggestion?: string;
}

// Consistency checking types
export interface Entity {
  name: string;
  type: 'character' | 'place' | 'date' | 'event' | 'object' | 'other';
  attributes: Record<string, string>;
  firstMention: {
    chapterId: string;
    chapterTitle: string;
    context: string;
  };
}

export interface Inconsistency {
  entity: string;
  type: 'attribute_conflict' | 'timeline_error' | 'character_contradiction' | 'plot_hole' | 'other';
  severity: 'minor' | 'moderate' | 'major';
  description: string;
  locations: Array<{
    chapterId: string;
    chapterTitle: string;
    excerpt: string;
  }>;
  suggestion?: string;
}

export interface ConsistencyReport {
  entities: Entity[];
  inconsistencies: Inconsistency[];
  summary: {
    totalEntities: number;
    totalInconsistencies: number;
    byType: Record<string, number>;
    bySeverity: Record<string, number>;
  };
}

// Add-on types
export interface UserAddon {
  id: string;
  userId: string;
  addonId: string;
  cycle: 'monthly' | 'annual';
  status: 'active' | 'canceled';
  currentPeriodEnd: string;
  createdAt: string;
}

// Notification types
export type NotificationType =
  | 'message'
  | 'mention'
  | 'collaboration_invite'
  | 'project_shared'
  | 'comment'
  | 'review'
  | 'hire_request'
  | 'system';

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  data: {
    conversationId?: string;
    projectId?: string;
    chapterId?: string;
    userId?: string;
    url?: string;
    [key: string]: unknown;
  };
  read: boolean;
  createdAt: string;
}

// Bible entry types
export type BibleEntryType = 'character' | 'location' | 'lore' | 'item' | 'organization' | 'note';

export interface BibleEntry {
  id: string;
  projectId: string;
  type: BibleEntryType;
  name: string;
  content: string;
  attributes: Record<string, string> | null;
  imageUrl?: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBibleEntryData {
  type: BibleEntryType;
  name: string;
  content: string;
  attributes?: Record<string, string> | null;
  imageUrl?: string | null;
  sortOrder?: number;
}

// Board types
export interface BoardCard {
  id: string;
  projectId: string;
  columnId: string;
  title: string;
  notes: string | null;
  color: string | null;
  sortOrder: number;
  chapterId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BoardColumn {
  id: string;
  projectId: string;
  title: string;
  sortOrder: number;
  createdAt: string;
  cards: BoardCard[];
}

export interface CreateCardData {
  columnId: string;
  title: string;
  notes?: string | null;
  color?: string | null;
  chapterId?: string | null;
}

export interface UpdateCardData {
  title?: string;
  notes?: string | null;
  color?: string | null;
  chapterId?: string | null;
}

// Member / Collaboration types
export type MemberRole = 'editor' | 'commenter' | 'viewer';
export type MemberStatus = 'invited' | 'active' | 'declined';

export interface ProjectMemberOwner {
  id: string;
  email: string;
  name: string;
  role: 'owner';
  status: 'active';
  isOwner: true;
}

export interface ProjectMemberEntry {
  id: string;
  email: string;
  name: string;
  role: MemberRole;
  status: MemberStatus;
  isOwner: false;
  userId?: string | null;
  createdAt: string;
  acceptedAt?: string | null;
}

export interface InviteInfo {
  id: string;
  email: string;
  role: MemberRole;
  status: MemberStatus;
  projectId: string;
  projectTitle: string;
  inviterName: string;
}

// Chapter version history types
export type ChapterVersionLabel = 'manual' | 'autosave' | 'pre-ai' | 'restore';

export interface ChapterVersionMeta {
  id: string;
  label: ChapterVersionLabel;
  wordCount: number;
  createdAt: string;
  createdBy: string | null;
  creatorName: string | null;
}

export interface ChapterVersionFull extends ChapterVersionMeta {
  chapterId: string;
  projectId: string;
  content: string;
}

export const api = new ApiClient(API_BASE);
